import React, { useState, useEffect, useCallback } from 'react';
import { getCombos, createCombo, updateCombo, deleteCombo, getRescueActions } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Loader2, X, Star } from 'lucide-react';

const GI_IMPACTS = [
  'Reduces GI by 10-20%',
  'Reduces GI by 20-30%',
  'Reduces GI by 30-40%',
  'Protein buffers sugar',
  'Cooling creates resistant starch',
  'Fat slows absorption',
  'Fiber reduces spike',
];

const emptyCombo = {
  name: '',
  emoji: '',
  foods: [],
  rescueActionIds: [],
  giImpact: GI_IMPACTS[0],
  effectiveGI: 50,
  notes: '',
  tags: [],
  isFeatured: false,
  isActive: true,
};

const SmartCombosPage = () => {
  const [combos, setCombos] = useState([]);
  const [allActions, setAllActions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [formData, setFormData] = useState(emptyCombo);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [foodInput, setFoodInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [combosRes, actionsRes] = await Promise.all([
        getCombos({}),
        getRescueActions()
      ]);
      setCombos(combosRes.data);
      setAllActions(actionsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openNewPanel = () => {
    setEditingCombo(null);
    setFormData(emptyCombo);
    setErrors({});
    setPanelOpen(true);
  };

  const openEditPanel = (combo) => {
    setEditingCombo(combo);
    setFormData({
      ...combo,
      foods: combo.foods || [],
      rescueActionIds: combo.rescueActionIds || [],
      tags: combo.tags || [],
    });
    setErrors({});
    setPanelOpen(true);
  };

  const addFood = () => {
    if (foodInput.trim()) {
      setFormData({ ...formData, foods: [...formData.foods, foodInput.trim()] });
      setFoodInput('');
    }
  };

  const removeFood = (index) => {
    setFormData({ ...formData, foods: formData.foods.filter((_, i) => i !== index) });
  };

  const addTag = () => {
    if (tagInput.trim()) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (index) => {
    setFormData({ ...formData, tags: formData.tags.filter((_, i) => i !== index) });
  };

  const toggleAction = (actionId) => {
    const newIds = formData.rescueActionIds.includes(actionId)
      ? formData.rescueActionIds.filter(id => id !== actionId)
      : [...formData.rescueActionIds, actionId];
    setFormData({ ...formData, rescueActionIds: newIds });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = true;
    if (!formData.emoji.trim()) newErrors.emoji = true;
    if (formData.foods.length === 0) newErrors.foods = true;
    if (!formData.notes.trim()) newErrors.notes = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      if (editingCombo) {
        await updateCombo(editingCombo.id, formData);
        toast.success('Smart combo updated successfully');
      } else {
        await createCombo(formData);
        toast.success('Smart combo created successfully');
      }
      setPanelOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error saving — try again');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingCombo) return;
    setIsSaving(true);
    try {
      await deleteCombo(editingCombo.id);
      toast.success('Smart combo deleted successfully');
      setPanelOpen(false);
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error deleting — try again');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="combos-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Smart Combos</h1>
        <Button onClick={openNewPanel} className="bg-[#1A6B3C] hover:bg-[#155a32]" data-testid="new-combo-btn">
          <Plus size={16} className="mr-1" /> New Combo
        </Button>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : combos.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 mb-4">No smart combos found</p>
            <Button onClick={openNewPanel} className="bg-[#1A6B3C] hover:bg-[#155a32]">
              <Plus size={16} className="mr-1" /> Add First Combo
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-12"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>GI Impact</TableHead>
                <TableHead className="text-center">Effective GI</TableHead>
                <TableHead className="text-center">Featured</TableHead>
                <TableHead className="text-center">Active</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {combos.map((combo) => (
                <TableRow key={combo.id} data-testid={`combo-row-${combo.id}`}>
                  <TableCell className="text-xl">{combo.emoji}</TableCell>
                  <TableCell className="font-medium">{combo.name}</TableCell>
                  <TableCell className="text-sm text-gray-600">{combo.giImpact}</TableCell>
                  <TableCell className="text-center">{combo.effectiveGI}</TableCell>
                  <TableCell className="text-center">
                    {combo.isFeatured ? <Star className="inline text-yellow-500" size={16} fill="currentColor" /> : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${combo.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => openEditPanel(combo)}>
                      <Pencil size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="slide-panel-520 overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingCombo ? 'Edit Smart Combo' : 'New Smart Combo'}</SheetTitle>
            <SheetDescription>
              {editingCombo ? 'Modify food pairing combination' : 'Create a new smart food combination'}
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label className={errors.name ? 'text-red-500' : ''}>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={errors.name ? 'border-red-500' : ''}
                />
              </div>
              <div>
                <Label className={errors.emoji ? 'text-red-500' : ''}>Emoji *</Label>
                <Input
                  value={formData.emoji}
                  onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
                  placeholder="🥜"
                  className={errors.emoji ? 'border-red-500' : ''}
                />
              </div>
            </div>

            {/* Foods */}
            <div>
              <Label className={errors.foods ? 'text-red-500' : ''}>Foods *</Label>
              <div className="tag-input-container mt-1">
                {formData.foods.map((food, idx) => (
                  <span key={idx} className="tag-chip">
                    {food}
                    <button onClick={() => removeFood(idx)}><X size={12} /></button>
                  </span>
                ))}
                <input
                  type="text"
                  value={foodInput}
                  onChange={(e) => setFoodInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFood())}
                  placeholder="Type food & Enter"
                  className="flex-1 min-w-[100px] border-none outline-none text-sm"
                />
              </div>
            </div>

            {/* Rescue Actions */}
            <div>
              <Label>Linked Rescue Actions</Label>
              <div className="mt-2 space-y-2">
                {allActions.map(action => (
                  <label key={action.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.rescueActionIds.includes(action.id)}
                      onChange={() => toggleAction(action.id)}
                      className="rounded"
                    />
                    <span className="text-sm">{action.icon} {action.title}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GI Impact *</Label>
                <Select value={formData.giImpact} onValueChange={(v) => setFormData({ ...formData, giImpact: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GI_IMPACTS.map(impact => (
                      <SelectItem key={impact} value={impact}>{impact}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Effective GI *</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={formData.effectiveGI}
                  onChange={(e) => setFormData({ ...formData, effectiveGI: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <Label className={errors.notes ? 'text-red-500' : ''}>Notes *</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className={errors.notes ? 'border-red-500' : ''}
              />
            </div>

            {/* Tags */}
            <div>
              <Label>Tags</Label>
              <div className="tag-input-container mt-1">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="tag-chip">
                    {tag}
                    <button onClick={() => removeTag(idx)}><X size={12} /></button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Type tag & Enter"
                  className="flex-1 min-w-[100px] border-none outline-none text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={formData.isFeatured} onCheckedChange={(v) => setFormData({ ...formData, isFeatured: v })} />
                <Label>Featured</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.isActive} onCheckedChange={(v) => setFormData({ ...formData, isActive: v })} />
                <Label>Active</Label>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t">
              <Button onClick={handleSave} disabled={isSaving} className="bg-[#1A6B3C] hover:bg-[#155a32]">
                {isSaving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Save
              </Button>
              {editingCombo && (
                <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} disabled={isSaving}>
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setPanelOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SmartCombosPage;
